/**
 * stack-ops.ts - Item 30 (v0.2.4.3)
 *
 * Pure functions for stack creation, membership management, and disbanding.
 * Each function takes the current DemoState (or a subset) and returns a
 * StatePatch with only the changed fields. No mutation of input data; no DOM;
 * no side effects.
 *
 * Principle A4 (plan): every stack operation is a pure function
 *   (state, args) → StatePatch
 *
 * Portability: this file is under apps/demo/src/util/ and is covered by the
 * check:portable directory-scope rule. No document/window/fetch/Node globals
 * are used.
 */

import type { DemoState, RegionEntry, RegionStack, StatePatch } from '../state';
import { generateUuid } from './uuid';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Compute the lowest unused positive integer suffix for a default stack name.
 * Looks at existing stacks whose userName matches "Stack {n}" and finds the
 * smallest positive integer n not already in use.
 *
 * Example: stacks named ["Stack 1", "Stack 3"] → returns 2.
 * Example: no stacks → returns 1.
 *
 * The caller is responsible for the locale-aware label prefix. This function
 * operates on an arbitrary string prefix (typically "Stack") and returns the
 * integer portion only.
 *
 * @param stacks  - Current regionStacks array.
 * @param prefix  - Name prefix to look for (e.g. "Stack").
 * @returns The lowest unused sequence integer (1-based).
 */
export function nextUnusedStackN(stacks: RegionStack[], prefix: string): number {
  // Collect the integer suffixes of stacks whose names match "{prefix} {n}".
  const usedNs = new Set<number>();
  for (const s of stacks) {
    if (s.userName.startsWith(prefix + ' ')) {
      const rest = s.userName.slice(prefix.length + 1);
      const n = parseInt(rest, 10);
      if (!isNaN(n) && String(n) === rest) {
        usedNs.add(n);
      }
    }
  }

  // Find the lowest unused integer starting from 1.
  let n = 1;
  while (usedNs.has(n)) n++;
  return n;
}

// ---------------------------------------------------------------------------
// CreateStackInput
// ---------------------------------------------------------------------------

export interface CreateStackInput {
  /**
   * UUIDs of regions to bundle into the new stack. Must be non-empty.
   * Members already in another stack are MOVED (their stackId is overwritten).
   * Order is preserved.
   */
  memberUuids: string[];
  /**
   * Display name for the new stack. If omitted, defaults to "Stack N" where
   * N is the next unused sequence number computed from existing stack names.
   * For locale-aware naming, the caller should pass an explicit userName (e.g.
   * from t('label.stackN', { n })) rather than relying on this fallback.
   */
  userName?: string;
}

// ---------------------------------------------------------------------------
// createStack
// ---------------------------------------------------------------------------

/**
 * Create a new stack containing the given regions. Generates a fresh UUID.
 * If a region was already in a different stack, it is moved to the new one.
 * Returns a patch with regionStacks and regionEntries updated.
 *
 * Returns an empty patch ({}) when memberUuids is empty (no-op).
 * Silently skips member UUIDs not present in regionEntries.
 */
export function createStack(state: DemoState, input: CreateStackInput): StatePatch {
  const { memberUuids, userName } = input;
  if (memberUuids.length === 0) return {};

  const memberSet = new Set<string>(memberUuids);
  const newUuid = generateUuid();

  // Resolve the display name - use the provided name, or fall back to "Stack N".
  const resolvedName =
    userName !== undefined && userName !== ''
      ? userName
      : `Stack ${nextUnusedStackN(state.regionStacks, 'Stack')}`;

  const newStack: RegionStack = {
    uuid: newUuid,
    userName: resolvedName,
    override: null,
  };

  // Append the new stack.
  const regionStacks = [...state.regionStacks, newStack];

  // Update entries: set stackId to the new uuid for each member UUID found
  // in regionEntries. Members not found are silently skipped.
  const regionEntries: RegionEntry[] = state.regionEntries.map((entry) =>
    memberSet.has(entry.uuid) ? { ...entry, stackId: newUuid } : entry,
  );

  return { regionStacks, regionEntries };
}

// ---------------------------------------------------------------------------
// addRegionsToStack
// ---------------------------------------------------------------------------

/**
 * Add regions to an existing stack (move them if they were in a different
 * stack). Returns an empty patch ({}) when:
 *   - stackUuid does not exist in regionStacks, or
 *   - memberUuids is empty.
 * Silently skips member UUIDs not present in regionEntries.
 */
export function addRegionsToStack(
  state: DemoState,
  stackUuid: string,
  memberUuids: string[],
): StatePatch {
  if (memberUuids.length === 0) return {};

  const stackExists = state.regionStacks.some((s) => s.uuid === stackUuid);
  if (!stackExists) return {};

  const memberSet = new Set<string>(memberUuids);

  const regionEntries: RegionEntry[] = state.regionEntries.map((entry) =>
    memberSet.has(entry.uuid) ? { ...entry, stackId: stackUuid } : entry,
  );

  return { regionEntries };
}

// ---------------------------------------------------------------------------
// removeRegionsFromStack
// ---------------------------------------------------------------------------

/**
 * Remove regions from their current stacks (set RegionEntry.stackId = null).
 * Does NOT delete the stack itself even if it becomes empty - use disbandStack
 * for that.
 *
 * Per-entry overrides are preserved. Silently skips member UUIDs not present
 * in regionEntries.
 */
export function removeRegionsFromStack(
  state: DemoState,
  memberUuids: string[],
): StatePatch {
  if (memberUuids.length === 0) return {};

  const memberSet = new Set<string>(memberUuids);

  const regionEntries: RegionEntry[] = state.regionEntries.map((entry) =>
    memberSet.has(entry.uuid) ? { ...entry, stackId: null } : entry,
  );

  return { regionEntries };
}

// ---------------------------------------------------------------------------
// disbandStack
// ---------------------------------------------------------------------------

/**
 * Disband the stack: set all members' stackId = null, drop the stack from
 * regionStacks. Per-entry overrides ARE preserved (they take precedence in
 * resolveRegionMethods anyway). The stack-level override is discarded.
 *
 * Returns an empty patch ({}) when stackUuid is not found in regionStacks.
 */
export function disbandStack(state: DemoState, stackUuid: string): StatePatch {
  const stackExists = state.regionStacks.some((s) => s.uuid === stackUuid);
  if (!stackExists) return {};

  // Remove the stack from regionStacks.
  const regionStacks = state.regionStacks.filter((s) => s.uuid !== stackUuid);

  // Clear stackId on all entries that belonged to this stack.
  const regionEntries: RegionEntry[] = state.regionEntries.map((entry) =>
    entry.stackId === stackUuid ? { ...entry, stackId: null } : entry,
  );

  return { regionStacks, regionEntries };
}
